/**
 * QuickActivityBar - Barra de ícones de atalho para atividades rápidas
 * Permite criar rapidamente atividades clicando nos ícones
 * Inclui ícone de agendamento vinculado ao deal
 * 
 * Versão: 1.1.0
 * RELEASE: 2026-02-14 - Adicionado ícone de agendamento (scheduling)
 */
import React from 'react';
import { Phone, Calendar, Mail, CheckSquare, FileText, CalendarClock, FileSignature } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Tipos de atividade CRM (criadas como atividade no deal)
export type CrmActivityType = 'call' | 'meeting' | 'email' | 'task' | 'note';

// Tipos completos da barra (inclui ações especiais)
export type QuickActivityType = CrmActivityType | 'scheduling' | 'contracts';

// Props do componente
interface QuickActivityBarProps {
  onActivityClick: (type: QuickActivityType) => void;
  disabled?: boolean;
  className?: string;
}

// Configuração dos ícones de atividade com cores permanentes
const activityIcons = [
  {
    type: 'call' as QuickActivityType,
    icon: Phone,
    label: 'Ligação',
    activeClass: 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50',
  },
  {
    type: 'meeting' as QuickActivityType,
    icon: Calendar,
    label: 'Reunião',
    activeClass: 'bg-orange-100 text-orange-600 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50',
  },
  {
    type: 'scheduling' as QuickActivityType,
    icon: CalendarClock,
    label: 'Gerar link de agendamento',
    activeClass: 'bg-sky-100 text-sky-600 hover:bg-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:hover:bg-sky-900/50',
  },
  {
    type: 'email' as QuickActivityType,
    icon: Mail,
    label: 'E-mail',
    activeClass: 'bg-violet-100 text-violet-600 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:hover:bg-violet-900/50',
  },
  {
    type: 'task' as QuickActivityType,
    icon: CheckSquare,
    label: 'Lembrete',
    activeClass: 'bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50',
  },
  {
    type: 'note' as QuickActivityType,
    icon: FileText,
    label: 'Nota',
    activeClass: 'bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50',
  },
];

/**
 * Barra de ícones de atalho para criar atividades rapidamente
 */
export function QuickActivityBar({ 
  onActivityClick, 
  disabled = false,
  className 
}: QuickActivityBarProps) {
  return (
    <div className={cn('flex items-center justify-center gap-1 pt-1', className)}>
      {activityIcons.map((activity) => {
        const Icon = activity.icon;
        return (
          <Tooltip key={activity.type}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-7 w-7 rounded-lg transition-all',
                  activity.activeClass
                )}
                onClick={() => onActivityClick(activity.type)}
                disabled={disabled}
              >
                <Icon className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">{activity.label}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
